// Leads list with multi-select, bulk actions, inline editing, keyboard nav.
//
// Visual model: the table is a grid of editable cells. Owner/Pipeline/Status
// cells share one CellPill primitive so they read as a row of dropdowns. Per-
// row actions (kebab, call) only appear on hover/focus to keep the table
// quiet at rest. Sticky header + zebra striping aid scanning.
//
// Keyboard: ↑/↓ or j/k move focus, Enter opens drawer, x toggles select,
// / focuses search, Esc clears focus. The focused row gets a left accent bar.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { color, motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  Search, AlertCircle, Loader2, Upload, Trash2, X,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Download,
  GitBranch, Copy, UserPlus, Check, Pencil,
  Bell, CheckCircle2, AlertTriangle, Globe, Trophy, Clock, Mail, Video,
} from "lucide-react";
import { Tooltip, Dropdown } from "antd";
import { Button, Badge } from "../atoms";
import { formatCurrency as libFormatCurrency } from "../lib/currency";
import { websiteHref, mailtoHref } from "../lib/links";
import { useOrgCurrency } from "../hooks/useOrgCurrency";
import PageHeader from "../organisms/PageHeader";
import TableFooter from "../organisms/TableFooter";
import LeadDrawer from "../organisms/LeadDrawer";
import LeadFormModal from "../organisms/LeadFormModal";
import CopyLeadModal from "../organisms/leads/CopyLeadModal";
import CallButton from "../organisms/calls/CallButton";
import BulkImportLeadsModal from "../molecules/BulkImportLeadsModal";
import AddToPipelineMenu from "../molecules/AddToPipelineMenu";
import LeadsFilters from "../molecules/LeadsFilters";
import SavedViewsBar from "../molecules/SavedViewsBar";
import LostReasonModal from "../molecules/LostReasonModal";
import FollowUpScheduler from "../molecules/FollowUpScheduler";
import { dueLabel } from "./commandSignals.mjs";
import ConfirmDeleteModal from "../overlays/ConfirmDeleteModal";
import { MemberPickerPanel } from "../molecules/MemberPicker";
import { useTeamMembers, formatMember, useMyCrmRole } from "../hooks/useTeamMembers";
import {
  useLeads, useCrmHealth, fetchLeadsPage,
  useBulkUpdateLeads, useBulkDeleteLeads, useBulkRestoreLeads,
  useUpdateLead, useLeadActivities,
} from "../hooks/useLeads";
import { usePipelines, useBulkAddLeads, useRemoveLeadFromPipeline } from "../hooks/usePipelines";
import { useQuery } from "@tanstack/react-query";
import { crmApi } from "../api/client";
import { spring } from "../theme/motion";
import { useCrmBreakpoint } from "../hooks/useCrmBreakpoint";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import { useProjectMetaAccount, useSyncMetaLeads } from "../hooks/useMetaProjectAccount";
import { Facebook } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { S5_ORG_ID, getOrgId } from "../../../utils/organization";
import { resolveUserAvatar } from "../../../utils/userAvatar";

const PAGE_SIZE = 20;
const EXPORT_HARD_CAP = 1000;
const EXPORT_PAGE_SIZE = 200;

// 9-column grid:
// select · lead · [owner — owner-role only] · pipeline · status · activity · value · next follow-up · actions
// Owner column visibility is strictly gated on the OWNER role (role === 'owner'):
//   • Owner sees it (and can change owners inline)
//   • Manager / Agent / Viewer don't see the column at all
// This is narrower than "isAdmin" — managers explicitly opt out per product call.
// Activity is two counts — manual and automated touches — with the timeline
// behind a tooltip, so row heights stay even. Source and labels are static lead
// attributes rather than activity; they never earned a track of their own and
// now ride along in that tooltip.
// The last date track shows the NEXT follow-up, not the last touch: "in 2d"
// tells you what to do, where "Updated 4d ago" only told you what happened.
// Created/updated stamps moved into that cell's tooltip.
const GRID_COLS_WITH_OWNER = "32px minmax(200px, 1.3fr) 128px 146px 86px 96px 96px 132px 100px";
const GRID_COLS_NO_OWNER   = "32px minmax(200px, 1.3fr) 146px 86px 96px 96px 132px 100px";
const GRID_MIN_WIDTH_WITH_OWNER = "1096px";
const GRID_MIN_WIDTH_NO_OWNER   = "966px";

const statusTone = { open: "primary", hot: "danger", warm: "warning", cold: "info", won: "success", lost: "danger" };
const STATUS_OPTIONS = [
  { value: "",     label: "All"  },
  { value: "open", label: "Open" },
  { value: "hot",  label: "Hot"  },
  { value: "warm", label: "Warm" },
  { value: "cold", label: "Cold" },
  { value: "won",  label: "Won"  },
  { value: "lost", label: "Lost" },
];

// Local wrapper around lib/currency.js — accepts a fallback code so cells
// without their own lead.currency still render in the org's default.
function formatCurrency(n, ccy) {
  return libFormatCurrency(n || 0, ccy || "USD") || "0";
}

function formatErr(err) {
  if (!err) return "Unknown error";
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.error) return typeof data.error === "string" ? data.error : data.error.message || JSON.stringify(data.error);
  if (data?.message) return data.message;
  return err.message || "Request failed";
}

// Absolute date+time under the relative gap in the Next follow-up cell —
// "in 2d" says how soon, this says exactly when.
// Compact format: "May 14, 3:42 PM" (day-month + time).
function formatDateTime(d) {
  if (!d) return "Never";
  return new Date(d).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function initialsOf(s) {
  return (s || "?").split(/\s+/).filter(Boolean).map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

const fmtStamp = (d) => (d ? new Date(d).toLocaleString() : "—");

const isClosedLead = (lead) => lead?.status === "won" || lead?.status === "lost";

// The due moment of a PENDING follow-up, snooze included. Zero when the lead
// has no pending one — a completed or missed follow-up is history, and this
// column is only ever about the next approach, never the last one.
function pendingDueAt(lead) {
  const fu = lead?.followUp;
  if (!fu || fu.status !== "pending" || !fu.scheduledAt) return 0;
  return Math.max(
    new Date(fu.scheduledAt).getTime(),
    fu.snoozedUntil ? new Date(fu.snoozedUntil).getTime() : 0,
  );
}

// Everything that counts as "we dealt with this lead" — past follow-ups plus
// the conversations around them. The tooltip shows these so the column can stay
// a single future date without hiding the trail behind it.
const HISTORY_TYPES = new Set([
  "FOLLOWUP_SCHEDULED", "FOLLOWUP_COMPLETED", "FOLLOWUP_CLEARED", "FOLLOWUP_SNOOZED",
  "CALL_INITIATED", "CALL_COMPLETED", "CALL_FAILED", "CALL_RECEIVED", "CALL_MISSED",
  "CALL_LOGGED", "CALL_NOTE_ADDED",
  "MEETING_CREATED", "MEETING_CANCELED", "MEETING_TRANSCRIPT_ADDED", "MEETING_SUMMARY_READY",
  "EMAIL_SENT", "EMAIL_REPLIED", "EMAIL_RECEIVED",
  "NOTE_ADDED",
]);

const HISTORY_ICON = {
  FOLLOWUP_COMPLETED: CheckCircle2,
  FOLLOWUP_SNOOZED: Clock,
  FOLLOWUP_CLEARED: AlertTriangle,
};

// Lives inside the tooltip on purpose: antd doesn't mount overlay content until
// the tooltip first opens, so the request fires on hover rather than 50 times
// when the page loads.
function FollowUpHistoryTip({ lead }) {
  const { data, isLoading } = useLeadActivities(lead._id);
  const rows = React.useMemo(() => (
    (data?.activities || []).filter((a) => HISTORY_TYPES.has(a.type)).slice(0, 6)
  ), [data]);

  const due = pendingDueAt(lead);
  const closed = isClosedLead(lead);
  const fu = lead.followUp;

  return (
    <div style={{ fontSize: 12, lineHeight: "17px", maxWidth: 300 }}>
      <div style={{ fontWeight: 600 }}>
        {closed ? `No follow-up needed — lead is ${lead.status}`
          : due ? (due < Date.now() ? "Overdue follow-up" : "Next follow-up")
            : "No next follow-up set"}
      </div>
      {due > 0 && <div>{new Date(due).toLocaleString()}</div>}
      {fu?.snoozedUntil && <div style={{ opacity: 0.8 }}>Snoozed · resumes {fmtStamp(fu.snoozedUntil)}</div>}
      {!due && !closed && (
        <div style={{ opacity: 0.85, marginTop: 2 }}>
          Required while the lead is open. Click to pick a date.
        </div>
      )}

      <div style={{ marginTop: 8, paddingTop: 7, borderTop: "1px solid rgba(255,255,255,0.18)" }}>
        <div style={{ fontWeight: 600, marginBottom: 3 }}>Previously</div>
        {isLoading && <div style={{ opacity: 0.75 }}>Loading history…</div>}
        {!isLoading && rows.length === 0 && (
          <div style={{ opacity: 0.75 }}>Nothing logged yet.</div>
        )}
        {rows.map((a) => {
          const Icon = HISTORY_ICON[a.type] || null;
          return (
            <div key={a._id} style={{ display: "flex", gap: 5, marginTop: 3 }}>
              {Icon
                ? <Icon size={10} style={{ flexShrink: 0, marginTop: 4, opacity: 0.8 }} />
                : <span style={{ width: 3, height: 3, borderRadius: 99, background: "currentColor", opacity: 0.55, flexShrink: 0, marginTop: 7 }} />}
              <span style={{ minWidth: 0 }}>
                {a.description}
                <span style={{ opacity: 0.65 }}> · {formatDateTime(a.createdAt)}</span>
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 7, opacity: 0.7 }}>
        Lead created {fmtStamp(lead.createdAt)}
      </div>
    </div>
  );
}

// The Next follow-up column: strictly the next time we approach this lead.
// It never reports a completed follow-up, because the last thing we did is not
// the next thing we owe — that mix-up is what put past dates in this column.
// Won and lost leads are exempt; everything else that lands here without a date
// reads as an error you can fix in one click.
function NextFollowUpCell({ lead, onSchedule }) {
  const due = pendingDueAt(lead);
  const closed = isClosedLead(lead);
  const late = due > 0 && due < Date.now();

  // A scheduled meeting IS the next touch. When one lands before the follow-up
  // date (or there's no follow-up date at all), show the meeting instead — the
  // whole column answers "what happens next", and a call on Thursday beats a
  // reminder next week.
  const meetingAt = lead?.nextMeeting?.scheduledStart
    ? new Date(lead.nextMeeting.scheduledStart).getTime()
    : 0;
  const showMeeting = !closed && meetingAt > 0 && (due === 0 || meetingAt < due);

  if (showMeeting) {
    return (
      <Tooltip
        title={<FollowUpHistoryTip lead={lead} />}
        mouseEnterDelay={0.3}
        placement="topRight"
      >
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onSchedule?.(lead); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onSchedule?.(lead); }
          }}
          style={{ minWidth: 0, cursor: "pointer" }}
        >
          <div style={{
            display: "flex", alignItems: "center", gap: 4,
            fontSize: 12.5, fontWeight: 600, color: "var(--crm-primary)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            <Video size={11} style={{ flexShrink: 0 }} />
            {dueLabel(meetingAt, Date.now())}
          </div>
          <div
            title={lead.nextMeeting.title || "Meeting"}
            style={{ fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {formatDateTime(meetingAt)}
          </div>
        </div>
      </Tooltip>
    );
  }

  const label = closed ? "Not needed" : due ? dueLabel(due, Date.now()) : "Set a date";
  const Icon = closed ? null : due ? (late ? AlertTriangle : Bell) : AlertTriangle;
  const color = closed ? "var(--color-text-secondary)"
    : !due ? "var(--crm-danger)"
      : late ? "var(--crm-danger)" : "var(--crm-warning, #B45309)";

  return (
    <Tooltip
      title={<FollowUpHistoryTip lead={lead} />}
      mouseEnterDelay={0.3}
      placement="topRight"
    >
      <div
        role={closed ? undefined : "button"}
        tabIndex={closed ? undefined : 0}
        onClick={closed ? undefined : (e) => { e.stopPropagation(); onSchedule?.(lead); }}
        onKeyDown={closed ? undefined : (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onSchedule?.(lead); }
        }}
        style={{ minWidth: 0, cursor: closed ? "default" : "pointer", opacity: closed ? 0.6 : 1 }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 12.5, fontWeight: due || !closed ? 600 : 400, color,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {Icon && <Icon size={11} style={{ flexShrink: 0 }} />}
          {label}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {due > 0 ? formatDateTime(due) : closed ? "—" : "Required while open"}
        </div>
      </div>
    </Tooltip>
  );
}

// Under the lead's name: the company, then one small icon per way of reaching
// them. The raw email used to sit here as text, which ate the width a long
// company name needed and still wasn't clickable — an address you have to
// select and copy is not a contact method.
function ContactIcon({ href, label, icon: Icon, newTab }) {
  return (
    <Tooltip title={label} mouseEnterDelay={0.3}>
      <a
        href={href}
        target={newTab ? "_blank" : undefined}
        rel={newTab ? "noopener noreferrer" : undefined}
        aria-label={label}
        // The row itself opens the lead drawer, so these must not bubble.
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 18, height: 18, borderRadius: 4, flexShrink: 0,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "var(--color-text-secondary)", textDecoration: "none",
          transition: "color 120ms, background 120ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--crm-primary)";
          e.currentTarget.style.background = "var(--crm-primary-soft)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--color-text-secondary)";
          e.currentTarget.style.background = "transparent";
        }}
      >
        <Icon size={12} />
      </a>
    </Tooltip>
  );
}

function LeadSubline({ lead }) {
  const site = websiteHref(lead.website);
  const mail = mailtoHref(lead.email);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, marginTop: 2 }}>
      <span style={{
        fontSize: 12, color: "var(--color-text-secondary)",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {lead.brandName || lead.email || "—"}
      </span>
      {/* Only render what the lead actually has, so the icon row is a truthful
          signal of how reachable they are rather than a fixed set of stubs. */}
      {mail && <ContactIcon href={mail} label={lead.email} icon={Mail} />}
      {site && <ContactIcon href={site} label={lead.website} icon={Globe} newTab />}
    </div>
  );
}

function HealthBadge() {
  const { data, isLoading, isError } = useCrmHealth();
  if (isLoading) return <Badge tone="neutral" dot>connecting</Badge>;
  if (isError || !data?.ok) return <Badge tone="danger" dot>backend offline</Badge>;
  return <Badge tone="success" dot>backend live · v{data.version}</Badge>;
}

function StatusFilterPills({ value, onChange }) {
  const selected = STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];
  const items = STATUS_OPTIONS.map((o) => ({
    key: o.value === "" ? "__all__" : o.value,
    label: o.label,
  }));
  return (
    <Dropdown
      menu={{
        items,
        selectedKeys: [value === "" ? "__all__" : value],
        onClick: ({ key }) => onChange(key === "__all__" ? "" : key),
      }}
      trigger={["click"]}
    >
      <button type="button" className="tg-tool">
        <span className="tg-tool-value">{selected.label}</span>
        <ChevronDown size={13} />
      </button>
    </Dropdown>
  );
}

function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const STATUS_EXPORT_LABEL = {
  open: "Open",
  hot: "Hot",
  warm: "Warm",
  cold: "Cold",
  won: "Won",
  lost: "Lost",
};

function formatLeadStatusForExport(status) {
  const key = String(status || "open").toLowerCase();
  return STATUS_EXPORT_LABEL[key] || status || "Open";
}

function leadsToCsv(rows) {
  const cols = ["title", "brandName", "email", "phone", "website", "value", "currency", "status", "source", "labels", "createdAt", "updatedAt"];
  const header = cols.map((c) => (c === "status" ? "Status" : c)).join(",");
  const body = rows.map((l) => cols.map((c) => {
    if (c === "labels" && Array.isArray(l[c])) return csvCell(l[c].join("; "));
    if (c === "status") return csvCell(formatLeadStatusForExport(l[c]));
    return csvCell(l[c]);
  }).join(",")).join("\n");
  return `${header}\n${body}\n`;
}

function downloadCsv(text, filename) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function SortHeader({ label, field, sort, order, onSort, align = "left" }) {
  const active = sort === field;
  const Icon = active && order === "asc" ? ChevronUp : ChevronDown;
  return (
    <button
      onClick={() => onSort(field)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: "transparent", border: "none", padding: 0, cursor: "pointer",
        font: "inherit", color: "inherit", letterSpacing: "inherit",
        textTransform: "inherit", fontWeight: "inherit",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        width: "100%",
        opacity: active ? 1 : 0.85,
      }}
      title={`Sort by ${label}`}
    >
      <span>{label}</span>
      {active && <Icon size={12} />}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Popover plumbing
// ──────────────────────────────────────────────────────────────────────────

function useAnchoredPopover() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const place = () => {
      const r = btnRef.current.getBoundingClientRect();
      const menuW = menuRef.current?.offsetWidth || 240;
      const menuH = menuRef.current?.offsetHeight || 260;
      const margin = 8;
      let top = r.bottom + 4;
      if (top + menuH > window.innerHeight - margin) {
        top = Math.max(margin, r.top - menuH - 4);
      }
      let left = r.left;
      if (left + menuW > window.innerWidth - margin) left = window.innerWidth - menuW - margin;
      if (left < margin) left = margin;
      setCoords({ top, left });
    };
    place();
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return { open, setOpen, btnRef, menuRef, coords };
}

function PopoverShell({ menuRef, coords, onClose, children, minWidth = 200 }) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <>
      <div onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 1200 }} />
      <div
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: coords?.top ?? -9999,
          left: coords?.left ?? -9999,
          visibility: coords ? "visible" : "hidden",
          background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)",
          borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
          padding: 6, zIndex: 1201, minWidth,
          maxHeight: "min(420px, calc(100vh - 24px))", overflowY: "auto",
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Shared CellPill — uniform chrome for Owner / Pipeline / Status / etc.
// Dotted variant signals "empty / click to fill", solid variant signals
// "has a value". Hover always lifts to primary border + muted bg. Caret is
// always present so the table reads as a row of dropdowns.
// ──────────────────────────────────────────────────────────────────────────

const CellPill = React.forwardRef(function CellPill(
  { leading, label, empty, tone, onClick, title, disabled, hideCaret },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="crm-cell-pill"
      data-empty={empty ? "true" : "false"}
      data-tone={tone || "neutral"}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        width: "100%", maxWidth: "100%",
        padding: "4px 6px 4px 5px",
        background: "transparent",
        border: `1px ${empty ? "dashed" : "solid"} var(--color-border-subtle)`,
        borderRadius: 6,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 12, fontWeight: 500,
        color: empty ? "var(--color-text-secondary)" : "var(--color-text-primary)",
        textAlign: "left",
        transition: "background 120ms, border-color 120ms, color 120ms",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = "var(--color-bg-muted)";
        e.currentTarget.style.borderColor = "var(--crm-primary)";
        e.currentTarget.style.borderStyle = "solid";
        e.currentTarget.style.color = "var(--color-text-primary)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.borderColor = "var(--color-border-subtle)";
        e.currentTarget.style.borderStyle = empty ? "dashed" : "solid";
        e.currentTarget.style.color = empty ? "var(--color-text-secondary)" : "var(--color-text-primary)";
      }}
    >
      {leading}
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      {!hideCaret && <ChevronDown size={11} style={{ flexShrink: 0, opacity: 0.5 }} />}
    </button>
  );
});

// ──────────────────────────────────────────────────────────────────────────
// Owner cell
// ──────────────────────────────────────────────────────────────────────────

function OwnerCell({ lead }) {
  const { open, setOpen, btnRef, menuRef, coords } = useAnchoredPopover();
  const { byId } = useTeamMembers();
  const update = useUpdateLead();
  const owner = lead.ownerId ? byId.get(String(lead.ownerId)) : null;
  const label = owner ? formatMember(owner) : "Assign";
  const initials = owner ? initialsOf(formatMember(owner)) : null;

  const assign = (ownerId) => {
    update.mutate(
      { id: lead._id, patch: { ownerId: ownerId || null } },
      {
        onSuccess: () => { toast.success(ownerId ? "Owner assigned" : "Owner cleared"); setOpen(false); },
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };

  const ownerAvatar = owner ? resolveUserAvatar(owner) : null;

  const leading = ownerAvatar ? (
    <img src={ownerAvatar} alt="" style={{ width: 20, height: 20, borderRadius: 9999, objectFit: "cover", flexShrink: 0 }} />
  ) : (
    <span style={{
      width: 20, height: 20, borderRadius: 9999, flexShrink: 0,
      background: owner ? "var(--crm-primary-soft)" : "var(--color-bg-muted)",
      color: owner ? "var(--crm-primary)" : "var(--color-text-secondary)",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: 9, fontWeight: 600,
    }}>{owner ? initials : <UserPlus size={11} />}</span>
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <CellPill
        ref={btnRef}
        leading={leading}
        label={label}
        empty={!owner}
        onClick={() => setOpen((v) => !v)}
        title={owner ? `Owner: ${label} — click to change` : "Click to assign owner"}
      />
      {open && (
        <PopoverShell menuRef={menuRef} coords={coords} onClose={() => setOpen(false)} minWidth={260}>
          <div style={{ padding: 4 }}>
            <MemberPickerPanel value={lead.ownerId || null} onChange={assign} />
          </div>
        </PopoverShell>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Pipeline cell — shows current stage if the lead is on a pipeline, else
// "Add to pipeline". Menu allows moving across pipelines/stages and removing.
// Stage lookup is derived client-side from the existing usePipelines payload.
// ──────────────────────────────────────────────────────────────────────────

function buildLeadStageIndex(pipelinesData) {
  const map = new Map();
  const pipelines = pipelinesData?.pipelines || [];
  pipelines.forEach((p) => {
    (p.stages || []).forEach((s) => {
      (s.leads || []).forEach((entry) => {
        const id = String(entry.leadId || entry.lead?._id || "");
        if (!id) return;
        if (!map.has(id)) map.set(id, { pipelineId: p._id, pipelineName: p.name, stageId: s._id, stageName: s.name });
      });
    });
  });
  return map;
}

function PipelineCell({ lead, stageIndex }) {
  const { open, setOpen, btnRef, menuRef, coords } = useAnchoredPopover();
  const { data: pipelinesData } = usePipelines();
  const pipelines = pipelinesData?.pipelines || [];
  const bulkAdd = useBulkAddLeads();
  const removeLead = useRemoveLeadFromPipeline();
  const [activePipelineId, setActivePipelineId] = useState(null);
  const activePipeline = pipelines.find((p) => String(p._id) === String(activePipelineId)) || null;
  const close = () => { setOpen(false); setActivePipelineId(null); };
  const current = stageIndex.get(String(lead._id)) || null;

  const addToStage = (stageId) => {
    if (!activePipeline) return;
    if (!stageId) {
      // Defensive — guards against legacy pipeline docs whose stage subdocs
      // were saved without a Mongo-assigned _id. Surfaces the bad row to ops
      // instead of bubbling a cryptic backend 400 to the user.
      // eslint-disable-next-line no-console
      console.error("[CRM] pipeline stage missing _id", { pipeline: activePipeline });
      toast.error("This pipeline has a corrupted stage. Recreate it from Settings.");
      return;
    }
    bulkAdd.mutate(
      { pipelineId: activePipeline._id, leadIds: [lead._id], stageId, mode: "move" },
      {
        onSuccess: (res) => {
          const s = res?.summary || {};
          const verb = s.added ? "added" : s.moved ? "moved" : "updated";
          toast.success(`Lead ${verb}`);
          close();
        },
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };

  const removeFromPipeline = () => {
    if (!current) return;
    removeLead.mutate(
      { pipelineId: current.pipelineId, leadId: lead._id },
      {
        onSuccess: () => { toast.success("Removed from pipeline"); close(); },
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };

  const leading = (
    <span style={{
      width: 20, height: 20, borderRadius: 6, flexShrink: 0,
      background: current ? "var(--crm-primary-soft)" : "var(--color-bg-muted)",
      color: current ? "var(--crm-primary)" : "var(--color-text-secondary)",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}><GitBranch size={11} /></span>
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <CellPill
        ref={btnRef}
        leading={leading}
        label={current ? current.stageName : "Add to pipeline"}
        empty={!current}
        onClick={() => setOpen((v) => !v)}
        title={current ? `${current.pipelineName} · ${current.stageName} — click to change` : "Click to add to pipeline"}
      />
      {open && (
        <PopoverShell menuRef={menuRef} coords={coords} onClose={close} minWidth={240}>
          {pipelines.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: "var(--color-text-secondary)" }}>No pipelines yet.</div>
          )}
          {pipelines.length > 0 && !activePipelineId && (
            <>
              {current && (
                <div style={{ fontSize: 11, padding: "4px 10px 6px", color: "var(--color-text-secondary)" }}>
                  Currently on <strong>{current.pipelineName}</strong> · <strong>{current.stageName}</strong>
                </div>
              )}
              {pipelines.map((p) => (
                <RowMenuItem
                  key={p._id}
                  label={p.name}
                  trailing={<ChevronRight size={12} />}
                  onClick={() => setActivePipelineId(p._id)}
                />
              ))}
              {current && (
                <>
                  <div style={{ height: 1, background: "var(--color-border-subtle)", margin: "4px 2px" }} />
                  <RowMenuItem icon={<X size={13} />} label="Remove from pipeline" danger onClick={removeFromPipeline} disabled={removeLead.isPending} />
                </>
              )}
            </>
          )}
          {activePipeline && (
            <>
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", padding: "6px 10px 4px" }}>
                {activePipeline.name}
              </div>
              {(activePipeline.stages || []).map((st) => {
                const isCurrent = current && String(current.stageId) === String(st._id || st.id);
                return (
                  <RowMenuItem
                    key={st._id || st.id}
                    icon={isCurrent ? <Check size={13} /> : null}
                    label={st.name}
                    onClick={() => addToStage(st._id || st.id)}
                    disabled={bulkAdd.isPending}
                  />
                );
              })}
              <div style={{ height: 1, background: "var(--color-border-subtle)", margin: "4px 2px" }} />
              <RowMenuItem label="← Back" onClick={() => setActivePipelineId(null)} />
            </>
          )}
        </PopoverShell>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Status cell — renders the colored dot inline so the cell still pops, but
// inside the shared CellPill chrome.
// ──────────────────────────────────────────────────────────────────────────

const STATUS_DOT = {
  open: "var(--crm-primary)",
  hot:  "rgb(220,38,38)",
  warm: "rgb(180,83,9)",
  cold: "rgb(37,99,235)",
  won:  "var(--crm-success)",
  lost: "var(--crm-danger)",
};

function StatusCell({ lead }) {
  const { open, setOpen, btnRef, menuRef, coords } = useAnchoredPopover();
  const update = useUpdateLead();
  const [lostOpen, setLostOpen] = useState(false);
  const applyStatus = (status, lostReason) => {
    update.mutate(
      { id: lead._id, patch: lostReason ? { status, lostReason } : { status } },
      {
        onSuccess: () => { toast.success(`Marked ${status}`); setOpen(false); setLostOpen(false); },
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };
  const setStatus = (status) => {
    if (status === lead.status) return;
    if (status === "lost") {
      setOpen(false);
      setLostOpen(true);
      return;
    }
    applyStatus(status);
  };
  const leading = (
    <span style={{ width: 8, height: 8, borderRadius: 9999, background: STATUS_DOT[lead.status] || "var(--color-text-secondary)", flexShrink: 0, marginLeft: 3 }} />
  );
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <CellPill
        ref={btnRef}
        leading={leading}
        label={lead.status}
        onClick={() => setOpen((v) => !v)}
        title="Click to change status"
      />
      {open && (
        <PopoverShell menuRef={menuRef} coords={coords} onClose={() => setOpen(false)} minWidth={160}>
          {["open", "hot", "warm", "cold", "won", "lost"].map((s) => (
            <RowMenuItem
              key={s}
              icon={<span style={{ width: 8, height: 8, borderRadius: 9999, background: STATUS_DOT[s], display: "inline-block" }} />}
              label={<>Mark <strong>{s}</strong></>}
              onClick={() => setStatus(s)}
              disabled={update.isPending || lead.status === s}
              trailing={lead.status === s ? <Check size={12} /> : null}
            />
          ))}
        </PopoverShell>
      )}
      <LostReasonModal
        open={lostOpen}
        onClose={() => setLostOpen(false)}
        onSubmit={(reason) => applyStatus("lost", reason)}
        isPending={update.isPending}
        title={`Mark "${lead.title || "lead"}" as lost`}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Row actions (kebab)
// ──────────────────────────────────────────────────────────────────────────

// Hover-only "mark Won" button. Hidden once already won. Lost-state still
// uses the StatusCell pill — Won deserves a one-click because it's the
// terminal-positive outcome you want to make easy.
function WonQuickAction({ lead }) {
  const update = useUpdateLead();
  if (lead.status === "won") return null;
  return (
    <button
      type="button"
      title="Mark as Won"
      onClick={(e) => {
        e.stopPropagation();
        update.mutate(
          { id: lead._id, patch: { status: "won" } },
          {
            onSuccess: () => toast.success("Marked won"),
            onError: (err) => toast.error(formatErr(err)),
          },
        );
      }}
      disabled={update.isPending}
      style={{
        width: 26, height: 26, borderRadius: 6,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "transparent", border: "1px solid transparent",
        color: "var(--crm-success)", cursor: "pointer",
        transition: "background 120ms, border-color 120ms",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; e.currentTarget.style.borderColor = "var(--color-border-subtle)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
    >
      <Trophy size={14} />
    </button>
  );
}

function IconActionButton({ title, onClick, disabled, danger, children }) {
  const color = danger ? "var(--crm-danger)" : "var(--color-text-secondary)";
  return (
    <Tooltip title={title} mouseEnterDelay={0.2}>
      <button
        type="button"
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        style={{
          width: 26, height: 26, borderRadius: 6,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "transparent", border: "1px solid transparent",
          color, cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          transition: "background 120ms, border-color 120ms",
        }}
        onMouseEnter={(e) => { if (disabled) return; e.currentTarget.style.background = "var(--color-bg-muted)"; e.currentTarget.style.borderColor = "var(--color-border-subtle)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
      >
        {children}
      </button>
    </Tooltip>
  );
}

function LeadRowActions({ lead, onDeleted }) {
  const bulkDelete = useBulkDeleteLeads();
  const bulkRestore = useBulkRestoreLeads();
  const [copyOpen, setCopyOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const performDelete = () => {
    const ids = [lead._id];
    bulkDelete.mutate(ids, {
      onSuccess: () => {
        setConfirmOpen(false);
        onDeleted?.();
        toast.custom((t) => (
          <div style={{
            background: "var(--color-text-primary)", color: "var(--color-bg-surface)",
            borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)", fontSize: 14, maxWidth: 420,
          }}>
            <span>Lead deleted</span>
            <button
              onClick={() => {
                bulkRestore.mutate(ids, {
                  onSuccess: () => toast.success("Restored"),
                  onError: (err) => toast.error(formatErr(err)),
                });
                toast.dismiss(t.id);
              }}
              style={{
                background: "transparent", border: "1px solid currentColor",
                color: "inherit", borderRadius: 6, padding: "4px 10px",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >Undo</button>
            <button
              onClick={() => toast.dismiss(t.id)}
              aria-label="Dismiss"
              style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", display: "inline-flex" }}
            ><X size={14} /></button>
          </div>
        ), { duration: 6000 });
      },
      onError: (err) => toast.error(formatErr(err)),
    });
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 2 }} onClick={(e) => e.stopPropagation()}>
      <IconActionButton title="Copy to project" onClick={() => setCopyOpen(true)}>
        <Copy size={14} />
      </IconActionButton>
      <IconActionButton title="Delete lead" danger onClick={() => setConfirmOpen(true)} disabled={bulkDelete.isPending}>
        <Trash2 size={14} />
      </IconActionButton>
      <CopyLeadModal open={copyOpen} onClose={() => setCopyOpen(false)} lead={lead} />
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={performDelete}
        title="Delete this lead?"
        message={`"${lead.title || "Untitled lead"}" will be deleted`}
        confirmLabel="Delete lead"
        isPending={bulkDelete.isPending}
      />
    </div>
  );
}

function RowMenuItem({ icon, label, onClick, disabled, danger, trailing }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "8px 10px", borderRadius: 6, border: "none",
        background: "transparent", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1, textAlign: "left", fontSize: 13,
        color: danger ? "var(--crm-danger)" : "var(--color-text-primary)",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = "var(--color-bg-muted)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      {icon && <span style={{ display: "inline-flex", color: danger ? "var(--crm-danger)" : "var(--color-text-secondary)" }}>{icon}</span>}
      <span style={{ flex: 1 }}>{label}</span>
      {trailing}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Inline title edit — double-click name, Enter saves, Esc cancels.
// ──────────────────────────────────────────────────────────────────────────

function InlineTitle({ lead }) {
  const update = useUpdateLead();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(lead.title || "");
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setVal(lead.title || ""); }, [lead.title, editing]);
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

  const commit = () => {
    const trimmed = val.trim();
    if (!trimmed || trimmed === lead.title) { setEditing(false); setVal(lead.title || ""); return; }
    update.mutate(
      { id: lead._id, patch: { title: trimmed } },
      {
        onSuccess: () => { toast.success("Renamed"); setEditing(false); },
        onError: (err) => { toast.error(formatErr(err)); setEditing(false); setVal(lead.title || ""); },
      },
    );
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); setEditing(false); setVal(lead.title || ""); }
        }}
        style={{
          width: "100%", fontSize: 14, fontWeight: 500, lineHeight: "20px",
          padding: "2px 6px", margin: "-3px -7px",
          border: "1px solid var(--crm-primary)",
          borderRadius: 6, background: "var(--color-bg-surface)",
          color: "var(--color-text-primary)", outline: "none",
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="crm-inline-title"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
      title="Double-click to rename"
      style={{
        all: "unset",
        display: "block", width: "100%", cursor: "text",
        fontSize: 14, fontWeight: 500, lineHeight: "20px",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        color: "var(--color-text-primary)",
      }}
    >
      {lead.title || <span style={{ color: "var(--color-text-secondary)" }}>(untitled)</span>}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Per-column cells (Activity / Source / Labels) — extracted so the row stays
// readable. Each cell renders inside its grid track with overflow hidden so
// a long label doesn't shove neighbouring cells around.
// ──────────────────────────────────────────────────────────────────────────

// How much has happened on this lead, split by who did it: M = manual, a person
// acted; A = auto, a workflow or sync acted. Two numbers instead of the chip
// stack that used to live here, because "how worked is this lead" is the
// question the column is scanned for, and the lead's own attributes it used to
// show (next step, source, labels) are static facts that never earned a track.
// They survive under the counts in the tooltip.
const ORIGIN_STYLE = {
  manual: { label: "M", word: "manual", fg: "var(--crm-primary)", bg: "var(--crm-primary-soft)" },
  auto:   { label: "A", word: "auto",   fg: "#7C3AED",            bg: "rgba(124,58,237,0.12)" },
};

// The timeline is the point of the tooltip, so it gets most of the height. Past
// this, the lead drawer's Activity tab is the right place to read.
const TIMELINE_ROWS = 14;

function CountPill({ origin, n }) {
  const s = ORIGIN_STYLE[origin];
  return (
    <span style={{
      display: "inline-flex", alignItems: "baseline", gap: 3, flexShrink: 0,
      padding: "1px 6px", borderRadius: 4, background: s.bg, color: s.fg,
      fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums",
    }}>
      {n}<span style={{ fontSize: 10, fontWeight: 700, opacity: 0.75 }}>{s.label}</span>
    </span>
  );
}

// A one-line skeleton bar standing in for a timeline row while its real text
// is still in flight. Sized from lead.activityCounts, which the list response
// already carries — so the tooltip opens at (roughly) its final height instead
// of growing after the fetch resolves. That resize-after-open is what made
// antd's popup reposition mid-animation and briefly paint a stale, empty frame
// on top of the real content — the "blank row" artifact.
function SkeletonRow() {
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 3 }}>
      <span style={{ flexShrink: 0, width: 14, height: 15, borderRadius: 3, background: "var(--color-bg-muted)" }} />
      <span style={{ flex: 1, height: 13, marginTop: 1, borderRadius: 3, background: "var(--color-bg-muted)" }} />
    </div>
  );
}

// Same lazy-on-hover trick as FollowUpHistoryTip: antd only mounts overlay
// content when the tooltip first opens, so 20 rows on screen cost 0 requests.
function ActivityTimelineTip({ lead }) {
  const { data, isLoading } = useLeadActivities(lead._id);
  const all = data?.activities || [];
  const rows = all.slice(0, TIMELINE_ROWS);

  const counts = lead.activityCounts || { manual: 0, auto: 0 };
  const expectedRows = Math.max(1, Math.min(counts.manual + counts.auto, TIMELINE_ROWS));
  const emailFu = lead.emailFollowUpSummary?.worstStatus ? lead.emailFollowUpSummary : null;
  const source = lead.source && lead.source !== "Manually created" ? lead.source : null;
  const context = [
    lead.nextActivity ? `Next step: ${lead.nextActivity}` : null,
    emailFu ? `Email follow-up: ${String(emailFu.worstStatus).replace(/_/g, " ")}${emailFu.missedCount > 1 ? ` · ${emailFu.missedCount} missed` : ""}` : null,
    source ? `Source: ${source}` : null,
    lead.labels?.length ? `Labels: ${lead.labels.join(", ")}` : null,
  ].filter(Boolean);

  let lastDay = null;

  return (
    <div style={{
      width: 320, fontSize: 12, lineHeight: "17px",
      background: "var(--color-bg-surface)", color: "var(--color-text-primary)",
      border: "1px solid var(--color-border-subtle)", borderRadius: 10,
      boxShadow: "0 12px 32px rgba(0,0,0,0.35)", padding: "11px 13px",
    }}>
      <div style={{ fontWeight: 600, paddingBottom: 7, borderBottom: "1px solid var(--color-border-subtle)" }}>
        {counts.manual} manual · {counts.auto} automated
      </div>

      <div style={{ marginTop: 7, maxHeight: 260, overflowY: "auto" }}>
        {isLoading && Array.from({ length: expectedRows }).map((_, i) => <SkeletonRow key={i} />)}
        {!isLoading && rows.length === 0 && (
          <div style={{ color: "var(--color-text-secondary)" }}>Nothing logged yet.</div>
        )}
        {!isLoading && rows.map((a) => {
          // Manual and automated rows are deliberately interleaved in one list —
          // the reason to open this is to see how the two wove together.
          const s = ORIGIN_STYLE[a.origin] || ORIGIN_STYLE.manual;
          const day = new Date(a.createdAt).toDateString();
          const newDay = day !== lastDay;
          lastDay = day;
          return (
            <React.Fragment key={a._id}>
              {newDay && (
                <div style={{ marginTop: 6, marginBottom: 2, color: "var(--color-text-secondary)", fontSize: 11, fontWeight: 600 }}>
                  {new Date(a.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: 3 }}>
                <span style={{
                  flexShrink: 0, width: 14, textAlign: "center", marginTop: 1,
                  borderRadius: 3, background: s.bg, color: s.fg,
                  fontSize: 9, fontWeight: 700, lineHeight: "15px",
                }}>
                  {s.label}
                </span>
                <span style={{ minWidth: 0 }}>
                  {a.description || a.type.replace(/_/g, " ").toLowerCase()}
                  <span style={{ color: "var(--color-text-secondary)" }}>
                    {" · "}
                    {new Date(a.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    {a.user?.name ? ` · ${a.user.name}` : a.origin === "auto" ? " · automation" : ""}
                  </span>
                </span>
              </div>
            </React.Fragment>
          );
        })}
        {!isLoading && all.length > rows.length && (
          <div style={{ marginTop: 5, color: "var(--color-text-secondary)" }}>+{all.length - rows.length} earlier — open the lead to see all</div>
        )}
      </div>

      {context.length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 7, borderTop: "1px solid var(--color-border-subtle)", color: "var(--color-text-secondary)" }}>
          {context.map((line) => <div key={line}>{line}</div>)}
        </div>
      )}
    </div>
  );
}

function ActivityCell({ lead }) {
  const { manual = 0, auto = 0 } = lead.activityCounts || {};

  return (
    <Tooltip
      title={<ActivityTimelineTip lead={lead} />}
      mouseEnterDelay={0.25}
      placement="topLeft"
      arrow={false}
      destroyTooltipOnHide
      overlayInnerStyle={{ padding: 0, background: "transparent", boxShadow: "none" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap" }}>
        {manual === 0 && auto === 0
          ? <span style={{ fontSize: 12, color: "var(--color-text-secondary)", opacity: 0.5 }}>—</span>
          : (
            <>
              {manual > 0 && <CountPill origin="manual" n={manual} />}
              {auto > 0 && <CountPill origin="auto" n={auto} />}
            </>
          )}
      </div>
    </Tooltip>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Table
// ──────────────────────────────────────────────────────────────────────────

function LeadsTable({
  rows, selected, onToggleOne, onToggleAll, onSelect, allSelectedOnPage,
  sort, order, onSort, focusIndex, footer, onSchedule,
}) {
  const { data: pipelinesData } = usePipelines();
  const stageIndex = useMemo(() => buildLeadStageIndex(pipelinesData), [pipelinesData]);
  const { currency: orgCurrency } = useOrgCurrency();
  const { isOwner } = useMyCrmRole();
  const gridCols = isOwner ? GRID_COLS_WITH_OWNER : GRID_COLS_NO_OWNER;
  const gridMinWidth = isOwner ? GRID_MIN_WIDTH_WITH_OWNER : GRID_MIN_WIDTH_NO_OWNER;

  return (
    <div style={{
      background: "var(--color-bg-surface)",
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8, overflow: "auto",
      boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
    }}>
      <div style={{ minWidth: gridMinWidth }}>
      <div style={{
        position: "sticky", top: 0, zIndex: 2,
        display: "grid", gridTemplateColumns: gridCols, gap: 14,
        padding: "10px 18px",
        borderBottom: "1px solid var(--color-border-subtle)",
        background: "var(--color-bg-muted)",
        fontSize: 12,
        color: "var(--color-text-muted)", fontWeight: 400, alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input
            type="checkbox"
            checked={allSelectedOnPage && rows.length > 0}
            onChange={onToggleAll}
            style={{ cursor: "pointer", width: 15, height: 15 }}
            aria-label="Select all leads on page"
          />
        </div>
        <div><SortHeader label="Lead" field="title" sort={sort} order={order} onSort={onSort} /></div>
        {isOwner && <div>Owner</div>}
        <div>Pipeline</div>
        <div><SortHeader label="Status" field="status" sort={sort} order={order} onSort={onSort} /></div>
        <div>Activity</div>
        <div><SortHeader label="Value" field="value" sort={sort} order={order} onSort={onSort} /></div>
        {/* Not sortable: the backend's sort whitelist covers scalar lead fields,
            and ascending on a missing followUp would float every unscheduled
            lead to the top. The Follow-ups tab is the due-sorted view. */}
        <div>Next follow-up</div>
        <div />
      </div>
      {rows.map((lead, i) => {
        const checked = selected.has(lead._id);
        const focused = focusIndex === i;
        const zebra = i % 2 === 1 ? "var(--color-bg-muted-soft, transparent)" : "transparent";
        return (
          <motion.div
            key={lead._id || lead.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring.smooth, delay: Math.min(i, 8) * 0.02 }}
            data-row-index={i}
            className="crm-lead-row"
            style={{
              position: "relative",
              display: "grid", gridTemplateColumns: gridCols, gap: 14,
              alignItems: "center", padding: "10px 18px",
              borderBottom: "1px solid var(--color-border-subtle)",
              background: checked ? "var(--crm-primary-soft)" : zebra,
              cursor: "pointer",
            }}
            onClick={() => onSelect(lead)}
          >
            {focused && (
              <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
                background: "var(--crm-primary)",
              }} />
            )}
            <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggleOne(lead._id)}
                style={{ cursor: "pointer", width: 15, height: 15 }}
                aria-label={`Select ${lead.title}`}
              />
            </div>
            <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 9999, flexShrink: 0,
                background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 600, fontSize: 11,
              }}>
                {initialsOf(lead.brandName || lead.title)}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ minWidth: 0 }}><InlineTitle lead={lead} /></div>
                <LeadSubline lead={lead} />
              </div>
            </div>
            {isOwner && <div style={{ minWidth: 0 }}><OwnerCell lead={lead} /></div>}
            <div style={{ minWidth: 0 }}><PipelineCell lead={lead} stageIndex={stageIndex} /></div>
            <div style={{ minWidth: 0 }}><StatusCell lead={lead} /></div>
            <ActivityCell lead={lead} />
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", fontVariantNumeric: "tabular-nums" }}>
              {formatCurrency(lead.value, lead.currency || orgCurrency)}
            </div>
            <NextFollowUpCell lead={lead} onSchedule={onSchedule} />
            <div className="crm-row-actions"
              style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}
              onClick={(e) => e.stopPropagation()}>
              <CallButton compact leadId={lead._id} leadPhone={lead.phone} leadTitle={lead.title} />
              <LeadRowActions lead={lead} visible={focused} />
            </div>
          </motion.div>
        );
      })}
      </div>
      {footer}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Bulk action bar
// ──────────────────────────────────────────────────────────────────────────

function BulkActionBar({ count, leadIds, onClear }) {
  const bulkUpdate = useBulkUpdateLeads();
  const bulkDelete = useBulkDeleteLeads();
  const bulkRestore = useBulkRestoreLeads();
  const { isOwner } = useMyCrmRole();
  const [statusMenu, setStatusMenu] = useState(false);
  const [ownerMenu, setOwnerMenu] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const setOwner = (ownerId) => {
    bulkUpdate.mutate(
      { leadIds, patch: { ownerId: ownerId || null } },
      {
        onSuccess: (res) => {
          toast.success(`${res.summary?.updated || 0} reassigned`);
          setOwnerMenu(false); onClear();
        },
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };

  const applyStatus = (status, lostReason) => {
    bulkUpdate.mutate(
      { leadIds, patch: lostReason ? { status, lostReason } : { status } },
      {
        onSuccess: (res) => {
          toast.success(`${res.summary?.updated || 0} updated to ${status}`);
          setStatusMenu(false); setLostOpen(false); onClear();
        },
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };

  const setStatus = (status) => {
    if (status === "lost") {
      setStatusMenu(false);
      setLostOpen(true);
      return;
    }
    applyStatus(status);
  };

  const performDelete = () => {
    const ids = [...leadIds];
    const n = ids.length;
    bulkDelete.mutate(ids, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClear();
        toast.custom((t) => (
          <div style={{
            background: "var(--color-text-primary)", color: "var(--color-bg-surface)",
            borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)", fontSize: 14, maxWidth: 420,
          }}>
            <span>Deleted {n} lead{n === 1 ? "" : "s"}</span>
            <button
              onClick={() => {
                bulkRestore.mutate(ids, {
                  onSuccess: (r) => toast.success(`Restored ${r.summary?.restored || 0}`),
                  onError: (err) => toast.error(formatErr(err)),
                });
                toast.dismiss(t.id);
              }}
              style={{
                background: "transparent", border: "1px solid currentColor",
                color: "inherit", borderRadius: 6, padding: "4px 10px",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >Undo</button>
            <button
              onClick={() => toast.dismiss(t.id)}
              aria-label="Dismiss"
              style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", display: "inline-flex" }}
            ><X size={14} /></button>
          </div>
        ), { duration: 6000 });
      },
      onError: (err) => toast.error(formatErr(err)),
    });
  };

  return (
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      style={{
        position: "sticky", bottom: 16, left: 0, right: 0, marginTop: 16,
        background: "var(--color-text-primary)", color: "var(--color-bg-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 8, boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
        padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        zIndex: 5,
      }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{count} selected</span>
      <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.18)" }} />
      <div style={{ position: "relative" }}>
        <Button variant="ghost" size="sm" style={{color:'white'}} onClick={() => setStatusMenu((v) => !v)} disabled={bulkUpdate.isPending}>
          {bulkUpdate.isPending ? "Updating…" : "Set status"}
        </Button>
        {statusMenu && (
          <>
            <div onClick={() => setStatusMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 1200 }} />
            <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.14)", padding: 4, zIndex: 1201, minWidth: 168 }}>
              {["open", "hot", "warm", "cold", "won", "lost"].map((s) => (
                <RowMenuItem
                  key={s}
                  icon={<span style={{ width: 8, height: 8, borderRadius: 9999, background: STATUS_DOT[s], display: "inline-block" }} />}
                  label={<>Set to <strong>{s}</strong></>}
                  onClick={() => setStatus(s)}
                  disabled={bulkUpdate.isPending}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {isOwner && (
        <div style={{ position: "relative" }}>
          <Button variant="ghost" size="sm" style={{color:'white'}} onClick={() => setOwnerMenu((v) => !v)} disabled={bulkUpdate.isPending}>
            Assign owner
          </Button>
          {ownerMenu && (
            <>
              <div onClick={() => setOwnerMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 1200 }} />
              <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.14)", padding: 8, zIndex: 1201, minWidth: 280 }}>
                <MemberPickerPanel value={null} onChange={setOwner} />
              </div>
            </>
          )}
        </div>
      )}
      <AddToPipelineMenu leadIds={leadIds} onDone={onClear} />
      <Button variant="ghost" size="sm" style={{color:'white'}} leftIcon={<Copy size={14} />} onClick={() => setCopyOpen(true)}>
        Copy to project
      </Button>
      <Button variant="ghost" style={{color:'white'}} size="sm" leftIcon={<Trash2 size={14} />} onClick={() => setConfirmOpen(true)} disabled={bulkDelete.isPending}>
        {bulkDelete.isPending ? "Deleting…" : "Delete"}
      </Button>
      <div style={{ flex: 1 }} />
      <button onClick={onClear} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "inherit", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }} aria-label="Clear selection">
        Clear <X size={12} />
      </button>
      <CopyLeadModal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        leadIds={leadIds}
        onCopied={() => { setCopyOpen(false); onClear(); }}
      />
      <LostReasonModal
        open={lostOpen}
        onClose={() => setLostOpen(false)}
        onSubmit={(reason) => applyStatus("lost", reason)}
        isPending={bulkUpdate.isPending}
        count={count}
      />
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={performDelete}
        title={count === 1 ? "Delete this lead?" : `Delete ${count} leads?`}
        message={count === 1
          ? "The selected lead will be soft-deleted. You can restore from the trash for a limited time."
          : `${count} leads will be soft-deleted. You can restore from the trash for a limited time.`}
        confirmLabel={count === 1 ? "Delete lead" : `Delete ${count} leads`}
        isPending={bulkDelete.isPending}
      />
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────

export default function Leads() {
  const { pagePad, isMobile } = useCrmBreakpoint();
  const { projectId } = useSelectedProject();
  const { user } = useAuth();
  const showMetaButton = getOrgId(user) === S5_ORG_ID;
  const { data: metaAccount } = useProjectMetaAccount(showMetaButton && projectId ? projectId : null);
  const metaSyncMut = useSyncMetaLeads(projectId);
  const isMetaConnected = !!metaAccount?.connected;

  const handleMetaSync = async () => {
    try {
      const result = await metaSyncMut.mutateAsync();
      if (result.imported > 0) toast.success(`Fetched ${result.imported} new lead${result.imported === 1 ? "" : "s"} from Facebook`);
      else toast.success("No new leads from Facebook");
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Sync failed");
    }
  };

  // Deep link: /crm/leads?leadId=x opens that lead's drawer (used by the
  // Command Center's "Open lead" new-tab button). Param is consumed once.
  const [selectedId, setSelectedId] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const id = p.get("leadId");
      if (id) {
        p.delete("leadId");
        const q = p.toString();
        window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : "") + window.location.hash);
        return id;
      }
    } catch (_e) {}
    return null;
  });
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  // Lead whose follow-up date is being picked from the table, so the required
  // date can be fixed in place without opening the whole drawer.
  const [scheduleLead, setScheduleLead] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("updatedAt");
  const [order, setOrder] = useState("desc");
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeViewId, setActiveViewId] = useState(null);
  const [focusIndex, setFocusIndex] = useState(-1);
  const searchRef = useRef(null);

  const onChangeQuery = (v) => { setQuery(v); setPage(0); setActiveViewId(null); };
  const onChangeStatus = (v) => { setStatusFilter(v); setPage(0); setActiveViewId(null); };
  const onChangeFilters = (f) => { setFilters(f); setPage(0); setActiveViewId(null); };
  const onSort = (field) => {
    if (sort === field) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSort(field); setOrder(field === "title" ? "asc" : "desc"); }
    setPage(0);
    setActiveViewId(null);
  };

  const applyView = (cfg) => {
    setQuery(cfg.query || "");
    setStatusFilter(cfg.status || "");
    setFilters(cfg.filters || {});
    setSort(cfg.sort || "updatedAt");
    setOrder(cfg.order || "desc");
    setPage(0);
  };
  const currentViewConfig = { query, status: statusFilter, filters, sort, order };

  const { data, isLoading, isError, error, refetch, isFetching } = useLeads({
    query, status: statusFilter || undefined,
    filters,
    limit: PAGE_SIZE, skip: page * PAGE_SIZE,
    sort, order,
  });
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Deep-linked lead may live on another page/filter — fetch it by id then.
  const inPageLead = selectedId ? items.find((l) => l._id === selectedId) || null : null;
  const soloLeadQ = useQuery({
    queryKey: ["crm", "lead", selectedId],
    queryFn: async () => (await crmApi.get(`/leads/${selectedId}`)).data.lead,
    enabled: !!selectedId && !inPageLead && !isLoading,
  });
  const drawerLead = inPageLead || (selectedId ? soloLeadQ.data || null : null);

  const visibleIds = useMemo(() => items.map((i) => i._id), [items]);
  const allSelectedOnPage = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggleOne = useCallback((id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);
  const toggleAll = useCallback(() => setSelected((prev) => {
    const next = new Set(prev);
    if (allSelectedOnPage) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    return next;
  }), [allSelectedOnPage, visibleIds]);
  const clear = useCallback(() => setSelected(new Set()), []);
  const { isAdmin: isAdminRole } = useMyCrmRole();

  const selectedArr = useMemo(() => [...selected], [selected]);

  // Keyboard navigation. Listens on window but bails out if focus is in an
  // input/textarea/contenteditable so typing in search/forms still works.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const inField = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (e.key === "/" && !inField) {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (inField) return;
      if (creating || importing || drawerLead) return;
      if (items.length === 0) return;

      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setFocusIndex((i) => Math.min(items.length - 1, i < 0 ? 0 : i + 1));
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setFocusIndex((i) => Math.max(0, i < 0 ? 0 : i - 1));
      } else if (e.key === "Enter" && focusIndex >= 0) {
        e.preventDefault();
        setSelectedId(items[focusIndex]?._id || null);
      } else if (e.key === "x" && focusIndex >= 0) {
        e.preventDefault();
        toggleOne(items[focusIndex]._id);
      } else if (e.key === "Escape") {
        setFocusIndex(-1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items, focusIndex, toggleOne, creating, importing, drawerLead]);

  // Keep focusIndex in bounds when the row set changes.
  useEffect(() => { if (focusIndex >= items.length) setFocusIndex(items.length - 1); }, [items.length, focusIndex]);

  // Scroll focused row into view.
  useEffect(() => {
    if (focusIndex < 0) return;
    const el = document.querySelector(`[data-row-index="${focusIndex}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusIndex]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = [];
      const cap = Math.min(total, EXPORT_HARD_CAP);
      for (let skip = 0; skip < cap; skip += EXPORT_PAGE_SIZE) {
        const limit = Math.min(EXPORT_PAGE_SIZE, cap - skip);
        const res = await fetchLeadsPage({
          query, status: statusFilter || undefined, filters, sort, order, limit, skip,
        });
        all.push(...(res.items || []));
        if (!res.items?.length) break;
      }
      if (all.length === 0) { toast.error("Nothing to export"); return; }
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(leadsToCsv(all), `leads-${stamp}.csv`);
      toast.success(`Exported ${all.length} lead${all.length === 1 ? "" : "s"}${total > EXPORT_HARD_CAP ? ` (capped at ${EXPORT_HARD_CAP})` : ""}`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <style>{`
        .crm-lead-row .crm-row-actions > * { opacity: 1; }
        .crm-lead-row:hover { background: var(--color-bg-muted) !important; }
        .crm-inline-title:hover { text-decoration: underline; text-decoration-color: var(--color-border-subtle); text-underline-offset: 3px; }
      `}</style>

      <PageHeader
        title="All leads"
        count={!isLoading && total > 0 ? total : null}
        actions={
          <>
            {!isMobile && isAdminRole && (
              <Button variant="secondary" size="sm" leftIcon={<Download size={15} />} onClick={exportCsv} disabled={exporting || total === 0}>
                {exporting ? "Exporting…" : "Export"}
              </Button>
            )}
            {showMetaButton && projectId && isMetaConnected && isAdminRole && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={metaSyncMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Facebook size={14} />}
                onClick={handleMetaSync}
                disabled={metaSyncMut.isPending}
              >
                {metaSyncMut.isPending ? "Fetching…" : "Fetch from Meta"}
              </Button>
            )}
            <Button variant="secondary" size="sm" leftIcon={<Upload size={15} />} onClick={() => setImporting(true)}>Import</Button>
            <Button variant="primary" size="sm" onClick={() => setCreating(true)}>+ New lead</Button>
          </>
        }
        topRight={<>
          <label className="tg-tool tg-tool-search" style={{ maxWidth: 280 }}>
            <Search size={13} />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => onChangeQuery(e.target.value)}
              placeholder="Search leads, companies, emails…"
            />
          </label>
          <span className="tg-tool-sep" />
          <StatusFilterPills value={statusFilter} onChange={onChangeStatus} />
          <LeadsFilters filters={filters} onChange={onChangeFilters} />
          <SavedViewsBar
            current={currentViewConfig}
            onApply={applyView}
            activeViewId={activeViewId}
            onActiveChange={setActiveViewId}
          />
          {isFetching && !isLoading && <Loader2 size={13} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />}
        </>}
      />

      <div style={{ flex: 1, minHeight: 0, padding: isMobile ? `0 ${pagePad}px 20px` : "0 24px 20px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 40, justifyContent: "center", color: "var(--color-text-secondary)" }}>
            <Loader2 size={18} className="animate-spin" /> Loading leads…
          </div>
        )}
        {isError && (
          <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 16, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Couldn't load leads</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>{formatErr(error)}</div>
              <button onClick={() => refetch()} style={{ marginTop: 10, background: "transparent", border: "1px solid currentColor", color: "currentColor", borderRadius: 8, padding: "4px 10px", fontSize: 13, cursor: "pointer" }}>Retry</button>
            </div>
          </div>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <div style={{ padding: 56, textAlign: "center", color: "var(--color-text-secondary)", border: "1px dashed var(--color-border-subtle)", borderRadius: 8, background: "var(--color-bg-surface)" }}>
            <div style={{ fontSize: 16, marginBottom: 6, color: "var(--color-text-primary)", fontWeight: 600 }}>
              {query || statusFilter ? "No leads match the current filters." : "No leads in this org yet."}
            </div>
            {!query && !statusFilter && (
              <div style={{ fontSize: 13, marginBottom: 16 }}>
                Click <strong>Import</strong> to paste a CSV, or <strong>+ New lead</strong> to create one manually.
              </div>
            )}
            {!query && !statusFilter && (
              <div style={{ display: "inline-flex", gap: 8 }}>
                <Button variant="secondary" size="sm" leftIcon={<Upload size={14} />} onClick={() => setImporting(true)}>Import</Button>
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>+ New lead</Button>
              </div>
            )}
          </div>
        )}
        {!isLoading && !isError && items.length > 0 && (
          <LeadsTable
            rows={items}
            selected={selected}
            allSelectedOnPage={allSelectedOnPage}
            onToggleOne={toggleOne}
            onToggleAll={toggleAll}
            onSelect={(l) => setSelectedId(l._id)}
            onSchedule={setScheduleLead}
            sort={sort}
            order={order}
            onSort={onSort}
            focusIndex={focusIndex}
            footer={
              <TableFooter
                page={page + 1} pages={pageCount} total={total}
                pageSize={PAGE_SIZE} shown={items.length} label="leads"
                onChange={(p) => setPage(p - 1)}
              />
            }
          />
        )}

        {/* {!isLoading && !isError && items.length > 0 && !isMobile && (
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--color-text-secondary)", display: "flex", justifyContent: "center", gap: 14, opacity: 0.7 }}>
            <span><kbd style={kbd}>↑</kbd> <kbd style={kbd}>↓</kbd> move</span>
            <span><kbd style={kbd}>Enter</kbd> open</span>
            <span><kbd style={kbd}>x</kbd> select</span>
            <span><kbd style={kbd}>/</kbd> search</span>
            <span>double-click name to rename</span>
          </div>
        )} */}

        {selected.size > 0 && (
          <BulkActionBar count={selected.size} leadIds={selectedArr} onClear={clear} />
        )}
      </div>

      <LeadDrawer open={!!drawerLead} onClose={() => setSelectedId(null)} lead={drawerLead} />
      <LeadFormModal open={creating} onClose={() => setCreating(false)} />
      <BulkImportLeadsModal open={importing} onClose={() => setImporting(false)} />
      <FollowUpScheduler
        open={!!scheduleLead}
        lead={scheduleLead}
        onClose={() => setScheduleLead(null)}
      />
    </div>
  );
}

const kbd = {
  display: "inline-block",
  padding: "1px 5px",
  fontSize: 10,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 4,
  background: "var(--color-bg-surface)",
  color: "var(--color-text-secondary)",
  lineHeight: 1.4,
};
